import React, {useEffect, useMemo} from 'react';

import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import moment from 'moment';
import {Moment} from 'moment-tz';
import RRule, {ByWeekday, Frequency as FreqType, Options, Weekday} from 'rrule';

import {useCurrentUserState} from 'app/current-user/api';
import {ShowNonFieldFormErrors} from 'app/general/forms';
import {localiseDate, rruleFromString} from 'app/tasks/utils';
import {gt, gts, ngt} from 'app/utils/i18n';

import {CheckboxWithSubFields} from './CheckboxWithSubFields';
import {MonthSelectFields} from './MonthSelectFields';
import {RRuleRepetitionLabel} from './RRuleRepetitionLabel';
import {
  normalizeByWeekday,
  WEEKDAYS,
  WeekdaysCheckboxes,
} from './WeekdaysCheckboxes';

interface Props {
  dtstart: string;
  rrule: string;
  onChange: (rrule: string) => void;
}

const WORKDAYS: Array<number> = [
  RRule.MO,
  RRule.TU,
  RRule.WE,
  RRule.TH,
  RRule.FR,
].map((v) => v.weekday);

// week-of-month positions supported by the "Week of month" select: 1st..4th, or -1 for last
const WEEK_OF_MONTH_POSITIONS: Array<number> = [1, 2, 3, 4, -1];

function getWeekOfMonthLabel(pos: number): string {
  return gt(
    '%s week of the month',
    pos > 0 ? moment.localeData().ordinal(pos) : gt('last')
  );
}

export const RRuleForm = React.memo(function RRuleForm({
  dtstart,
  rrule: rruleStr,
  onChange,
}: Props): JSX.Element {
  const {timezone} = useCurrentUserState().data!;

  const userTimezoneMoment = useMemo(
    () => localiseDate(dtstart, timezone),
    [dtstart, timezone]
  );

  const rrule = useMemo(() => {
    return normalizeRRule(rruleFromString(rruleStr), userTimezoneMoment, true);
  }, [rruleStr, userTimezoneMoment, timezone]);

  const {options} = rrule;
  // rrule.js's `options` getter normalizes byweekday to plain weekday
  // numbers and drops per-day ordinals (e.g. "3WE" becomes null) - it's
  // meant for the library's own iteration engine, not for reading back what
  // was set. origOptions preserves the ordinals we actually need here.
  const monthlyByWeekday = rrule.origOptions.byweekday;

  const showWeekdaysFields = useMemo(() => {
    return (
      (options.freq === RRule.DAILY && options.count !== 1) ||
      options.freq === RRule.WEEKLY
    );
  }, [options.freq, options.count]);

  const isRunningForAllWorkdays = useMemo(() => {
    const byweekday = normalizeByWeekday(options.byweekday).map(
      (d) => d.weekday
    );
    return WORKDAYS.every((day) => byweekday.includes(day));
  }, [options.byweekday]);

  function getLocalisedRepeatOptions(
    freqtype: FreqType | undefined,
    n: number | undefined = 1
  ): string | undefined {
    if (freqtype == null) {
      return undefined;
    }

    return {
      [RRule.DAILY]: ngt('day', 'days', n),
      [RRule.WEEKLY]: ngt('week', 'weeks', n),
      [RRule.MONTHLY]: ngt('month', 'months', n),
      [RRule.YEARLY]: ngt('year', 'years', n),
    }[freqtype];
  }

  function updateRRule(options: Partial<Options>, merge = true): void {
    const updatedRRule = normalizeRRule(
      new RRule({
        ...(merge ? rrule.origOptions : {}),
        ...options,
      }),
      userTimezoneMoment,
      // remove dtstart we set in normalizeRRule,
      // it was required to render the first occurrences
      false
    );

    onChange(updatedRRule.toString());
  }

  useEffect(() => {
    // we use dtstart to validate and normalize the byweekday value,
    // so when dtstart changes (userTimezoneMoment would also change),
    // we send the updated rrule to the parent component
    // as byweekday could be updated
    if (
      options.freq === RRule.MONTHLY &&
      isRepeatsByWeekdayEnabled(monthlyByWeekday)
    ) {
      // keep "repeats by day of week" in sync with the due date: this effect
      // only runs when the due date itself changes (not on every unrelated
      // field edit), so re-deriving the weekday/week-of-month here won't
      // clobber a manual selection until the due date actually moves again.
      updateRRule(getRRuleMonthByWeekData(userTimezoneMoment));
    } else {
      updateRRule({});
    }
  }, [userTimezoneMoment]);

  /* updates RRULE frequency */
  function updateRRuleFreq(freqString: string) {
    if (freqString === '') {
      // if it's a once only task, remove all aditional options
      return updateRRule(
        {
          freq: RRule.DAILY,
          count: 1,
        },
        false
      );
    } else {
      return updateRRule({
        freq: parseInt(freqString),
        interval: options.interval ?? 1,
        count: null,
        byweekday: [],
      });
    }
  }

  return (
    <div>
      <div className="form-row">
        <div className="form-group col-md-4">
          <label>{gt('Repeat')}</label>
          <select
            className="form-control"
            value={options.count === 1 ? '' : options.freq ?? ''}
            onChange={(e) => updateRRuleFreq(e.target.value)}
          >
            <option value="">{gts('Once Only')}</option>
            <option value={RRule.DAILY}>{gts('Daily')}</option>
            <option value={RRule.WEEKLY}>{gts('Weekly')}</option>
            <option value={RRule.MONTHLY}>{gts('Monthly')}</option>
            <option value={RRule.YEARLY}>{gts('Yearly')}</option>
          </select>
        </div>
        {options.count !== 1 ? (
          <div className="form-group col-md-8">
            <label>{gt('Repeats every')}</label>
            <div className="form-inline">
              <input
                className="form-control col-3 mr-2"
                type="number"
                value={options.interval}
                onChange={(e) =>
                  updateRRule({
                    interval: Math.max(parseInt(e.target.value || '1'), 1),
                  })
                }
                placeholder="1"
              />
              <label>
                {getLocalisedRepeatOptions(options.freq, options.interval)}
              </label>
            </div>
          </div>
        ) : null}
      </div>

      <ShowNonFieldFormErrors errorSelector={(errors) => errors.rrule} />

      {showWeekdaysFields ? (
        <CheckboxWithSubFields
          label={gt('Weekdays only')}
          fieldId="workday-checkbox"
          checked={isRunningForAllWorkdays}
          onChange={(checked) =>
            updateRRule({
              byweekday: selectWorkdays(rrule, checked),
              interval: 1,
              freq: RRule.WEEKLY,
            })
          }
          opened={options.freq === RRule.WEEKLY}
        >
          <WeekdaysCheckboxes
            dueDate={userTimezoneMoment}
            byweekday={options.byweekday}
            onChange={(byweekday) => updateRRule({byweekday})}
          />
        </CheckboxWithSubFields>
      ) : null}

      {options.freq === RRule.MONTHLY ? (
        <CheckboxWithSubFields
          label={gt('Repeats by day of week')}
          fieldId="repeat-by-day-of-week-checkbox"
          checked={isRepeatsByWeekdayEnabled(monthlyByWeekday)}
          onChange={(checked) =>
            updateRRule(updateRRuleMonthByWeek(checked, userTimezoneMoment))
          }
          opened={isRepeatsByWeekdayEnabled(monthlyByWeekday)}
        >
          <>
            <div className="form-group">
              <label htmlFor="week-of-month-select">
                {gt('Week of month')}
              </label>
              <select
                id="week-of-month-select"
                className="form-control"
                value={getWeekOfMonthPosition(monthlyByWeekday)}
                onChange={(e) => {
                  const pos = parseInt(e.target.value, 10);
                  updateRRule({
                    byweekday: applyWeekOfMonthToAll(
                      normalizeByWeekday(monthlyByWeekday),
                      pos
                    ),
                  });
                }}
              >
                {WEEK_OF_MONTH_POSITIONS.map((pos) => (
                  <option key={pos} value={pos}>
                    {getWeekOfMonthLabel(pos)}
                  </option>
                ))}
              </select>
            </div>
            <WeekdaysCheckboxes
              dueDate={userTimezoneMoment}
              byweekday={monthlyByWeekday}
              onChange={(byweekday) =>
                // each selected day carries its own week-of-month ordinal
                // (e.g. "3WE"), so newly (de)selected days need that same
                // shared position applied - see getWeekOfMonthPosition below.
                updateRRule({
                  byweekday: applyWeekOfMonthToAll(
                    byweekday,
                    getWeekOfMonthPosition(monthlyByWeekday)
                  ),
                })
              }
            />
          </>
        </CheckboxWithSubFields>
      ) : null}

      {options.freq !== RRule.YEARLY && options.count !== 1 ? (
        <MonthSelectFields
          options={options}
          onChange={updateRRule}
          dueDate={userTimezoneMoment}
        />
      ) : null}

      <RRuleRepetitionLabel rrule={rrule} />
    </div>
  );
});

// here we do all required processing in the rrule object being created
function normalizeRRule(
  rrule: RRule,
  userTimezoneMoment: Moment,
  setDtstart: boolean
): RRule {
  let options: Partial<Options> = {...rrule.origOptions};

  // when normalizing the rrule for internal use, we set dtstart to render
  // first occurences. When normalizing to send to the parent component the dtstart
  // field is removed. Maybe in the future we can keep it so backend will use it.
  options.dtstart = setDtstart ? userTimezoneMoment.toDate() : undefined;

  if (options.freq === RRule.WEEKLY) {
    options.byweekday = ensureWeekDayIsValid(options, userTimezoneMoment);
  }

  if (options.freq === RRule.MONTHLY) {
    if (isRepeatsByWeekdayEnabled(options.byweekday)) {
      options.bymonthday = null;
      // byweekday is already set here (either by the user via the
      // weekday/week-of-month fields, or seeded once from the due date in
      // updateRRuleMonthByWeek when the checkbox was first enabled). Don't
      // re-derive it from userTimezoneMoment on every normalize pass, or the
      // user's selection (e.g. "2nd Wednesday") gets silently reset to
      // match whatever week/day the current due date happens to fall on.
      // Old rules stored the week-of-month separately as a shared BYSETPOS
      // instead of per-day - migrate that onto each day here so the UI
      // shows the right "week of month" for legacy tasks, and so we don't
      // end up with a stale BYSETPOS alongside the new per-day ordinals.
      options.byweekday = migrateLegacyBysetpos(
        options.byweekday,
        options.bysetpos
      );
      options.bysetpos = null;
    } else {
      options.byweekday = null;
      options.bymonthday = [userTimezoneMoment.date(), -1];
      options.bysetpos = 1;
    }
  }

  if (options.freq === RRule.YEARLY) {
    options.bymonthday = [userTimezoneMoment.date(), -1];
    options.bymonth = userTimezoneMoment.month() + 1;
    options.bysetpos = 1;
  }

  options.bymonth = ensureMonthIsValid(options.bymonth, userTimezoneMoment);

  options = pickBy(options, (v) => v !== null && v !== undefined);

  // let's exclude all the options we don't care about.
  options = pick(options, getValidOptionsForFreq(options.freq));

  return new RRule(options);
}

function ensureWeekDayIsValid(
  options: Partial<Options>,
  userTimezoneMoment: Moment
): Array<ByWeekday> | ByWeekday | null | undefined {
  // since the API don't trigger errors when assigning a weekly task
  // with no weekday defined, we will not allow the user to deselect
  // all weekdays. When they try to deselect the last weekday, the task.due
  // week day will be selected

  const {freq, byweekday} = options;

  if (freq === RRule.WEEKLY) {
    const dtstartWeekday = userTimezoneMoment.isoWeekday() - 1;
    const normalizedByWeekdays = normalizeByWeekday(byweekday);

    // be careful, the RRule API accepts an array of integers as byweekday value
    // but that will cause the RRule text description to not the use "weekdays" and
    // list all workdays instead. So always provide an array of Weekday instances as byweekday
    // to avoid that bug

    if (normalizedByWeekdays.length === 1) {
      // if a single week day is selected, make sure that's the same as the due date
      return [WEEKDAYS[dtstartWeekday]];
    }
    if (
      // if more than one week day is selected, ensure the due date weekday is in the selection
      normalizedByWeekdays.find((d) => d.weekday === dtstartWeekday) == null
    ) {
      return [...normalizedByWeekdays, WEEKDAYS[dtstartWeekday]];
    }

    return normalizedByWeekdays;
  }
  return byweekday;
}

function ensureMonthIsValid(
  bymonth: Options['bymonth'] | undefined,
  userTimezoneMoment: Moment
): Options['bymonth'] {
  const dtstartMonth = userTimezoneMoment.month() + 1;

  // null means all months, all good
  if (bymonth == null) {
    return null;
  }
  // ensure it's an array
  if (!Array.isArray(bymonth)) {
    bymonth = [bymonth];
  }

  if (bymonth.length === 0) {
    // zero items means all months, all good
    return bymonth;
  }

  if (bymonth.length === 1) {
    // if a single month is selected, make sure that's the same as the due date
    return [dtstartMonth];
  }

  if (bymonth.indexOf(dtstartMonth) == -1) {
    // if more than one month is selected, ensure the due date weekday is in the selection
    return [...bymonth, dtstartMonth];
  }
  return bymonth.sort((a, b) => a - b);
}

function isRepeatsByWeekdayEnabled(
  byweekday: Partial<Options>['byweekday']
): boolean {
  if (byweekday == null) {
    return false;
  }
  return Array.isArray(byweekday) ? byweekday.length > 0 : true;
}

function selectWorkdays({options}: RRule, add: boolean): Array<ByWeekday> {
  const byweekday = normalizeByWeekday(options.byweekday).map((d) => d.weekday);

  return add
    ? WORKDAYS.reduce((byweekday, day) => {
        if (byweekday.indexOf(day) === -1) {
          return [...byweekday, day];
        }
        return byweekday;
      }, byweekday)
    : byweekday.filter((d) => WORKDAYS.indexOf(d) == -1);
}

function updateRRuleMonthByWeek(
  enabled: boolean,
  userTimezoneMoment: Moment
): Partial<Options> {
  if (!enabled) {
    return {byweekday: undefined};
  }

  return getRRuleMonthByWeekData(userTimezoneMoment);
}

function getRRuleMonthByWeekData(userTimezoneMoment: Moment): Partial<Options> {
  const weekDay = userTimezoneMoment.isoWeekday() - 1;
  const monthDay = userTimezoneMoment.date();
  // clone before endOf: it mutates in place, and userTimezoneMoment is a
  // memoized object shared across the whole form (used as dtstart and as the
  // dueDate prop for WeekdaysCheckboxes/MonthSelectFields), so mutating it
  // here would corrupt every other reader for as long as the memo is cached.
  const monthLastDay = userTimezoneMoment.clone().endOf('month').date();
  let weekInMonth = Math.ceil(monthDay / 7);

  if (monthDay > monthLastDay - 7)
    // user wants pick a weekday of the last week
    weekInMonth = -1;
  return {
    // each byweekday entry carries its own week-of-month ordinal (e.g.
    // "3WE"), so multiple selected days resolve independently instead of
    // sharing one BYSETPOS position over their combined occurrences.
    byweekday: [WEEKDAYS[weekDay].nth(weekInMonth)],
  };
}

function getWeekOfMonthPosition(
  byweekday: Partial<Options>['byweekday']
): number {
  return normalizeByWeekday(byweekday)[0]?.n ?? 1;
}

function applyWeekOfMonthToAll(
  byweekday: Array<Weekday>,
  n: number
): Array<Weekday> {
  return byweekday.map((day) => day.nth(n));
}

function migrateLegacyBysetpos(
  byweekday: Partial<Options>['byweekday'],
  legacyBysetpos: Options['bysetpos'] | undefined
): Array<Weekday> {
  const normalized = normalizeByWeekday(byweekday);
  const legacyPos = Array.isArray(legacyBysetpos)
    ? legacyBysetpos[0]
    : legacyBysetpos;

  if (legacyPos != null && normalized.every((d) => d.n == null)) {
    return normalized.map((d) => d.nth(legacyPos));
  }

  return normalized;
}

function getValidOptionsForFreq(freq: FreqType | undefined): Array<string> {
  if (freq == null) {
    return [];
  }

  const COMMONS = ['freq', 'interval', 'bymonth', 'dtstart'];

  return {
    [RRule.DAILY]: COMMONS.concat(['count']),
    [RRule.WEEKLY]: COMMONS.concat(['byweekday']),
    [RRule.MONTHLY]: COMMONS.concat(['byweekday', 'bymonthday', 'bysetpos']),
    [RRule.YEARLY]: COMMONS.concat(['bymonthday', 'bysetpos']),
  }[freq];
}
